import React, { useState, useEffect, useRef } from 'react';

import { Box, Flex, useOutsideClick } from '@chakra-ui/react';
import { Check, Close } from '@mesh.ai/rabbit';

import { StockTickerNegative, StockTickerPositive } from 'assets/assetsV1/iconComponents';

import { InputV2 } from 'uiToolkitV1/InputV2';
import { TextBodyMedium, TextLabelSmall } from 'uiToolkitV1/Typography';

export interface SalaryPercentageInputProps {
  /** The base/current value to calculate percentage against */
  baseValue: number;
  /** Initial proposed/target value */
  initialValue: number;
  /** Currency symbol to display (default: '$') */
  currencySymbol?: string;
  /** Unit suffix to display after the value (e.g., '/hr', '/month') */
  unitSuffix?: string;
  /** Minimum allowed percentage for guidelines */
  guidelineMin?: number;
  /** Maximum allowed percentage for guidelines */
  guidelineMax?: number;
  /** Width of the percentage input field */
  percentageInputWidth?: string;
  /** Whether to show the guideline validation message */
  showGuideline?: boolean;
  /** Custom guideline message */
  guidelineMessage?: string;
  /** Callback when values change */
  onChange?: (value: number, percentage: number) => void;
  /** Callback when user confirms the value */
  onConfirm?: (value: number, percentage: number) => void;
  /** Whether the inputs are disabled */
  disabled?: boolean;
  /** Custom validation function */
  isValid?: (value: number, percentage: number) => boolean;
  /** Custom error message */
  errorMessage?: string;
}

export const SalaryPercentageInput: React.FC<SalaryPercentageInputProps> = ({
  baseValue,
  initialValue,
  currencySymbol = '$',
  unitSuffix,
  guidelineMin,
  guidelineMax,
  percentageInputWidth = '100px',
  showGuideline = true,
  guidelineMessage,
  onChange,
  onConfirm,
  disabled = false,
  isValid,
  errorMessage,
}) => {
  const [value, setValue] = useState(initialValue);
  const [isPercentageFocused, setIsPercentageFocused] = useState(false);
  const [isSalaryFocused, setIsSalaryFocused] = useState(false);
  const [percentage, setPercentage] = useState<number>(() =>
    Number((((initialValue - baseValue) / baseValue) * 100).toFixed(2))
  );
  const [lastConfirmed, setLastConfirmed] = useState({ value: initialValue, percentage });

  const componentRef = useRef<HTMLDivElement>(null);

  let isOutsideGuideline = false;
  if (isValid) {
    isOutsideGuideline = !isValid(value, percentage);
  } else if (guidelineMin !== undefined && guidelineMax !== undefined) {
    isOutsideGuideline = percentage > guidelineMax || percentage < guidelineMin;
  }

  // Update percentage when baseValue or initialValue changes
  useEffect(() => {
    setValue(initialValue);
    const newPercentage = ((initialValue - baseValue) / baseValue) * 100;
    setPercentage(Number(newPercentage.toFixed(2)));
    setLastConfirmed({ value: initialValue, percentage: Number(newPercentage.toFixed(2)) });
  }, [baseValue, initialValue]);

  const handleValueChange = (newValue: number) => {
    setValue(newValue);
    const newPercentage = ((newValue - baseValue) / baseValue) * 100;
    const roundedPercentage = Number(newPercentage.toFixed(2));
    setPercentage(roundedPercentage);
    onChange?.(newValue, roundedPercentage);
  };

  const handlePercentageChange = (newPercentage: number) => {
    setPercentage(newPercentage);
    const newValue = Number(((100 + newPercentage) * (baseValue / 100)).toFixed(2));
    setValue(newValue);
    onChange?.(newValue, newPercentage);
  };

  const getGuidelineMessage = () => {
    if (guidelineMessage) return guidelineMessage;
    if (guidelineMin !== undefined && guidelineMax !== undefined) {
      return `Guideline value is ${guidelineMin}% - ${guidelineMax}%`;
    }
    return 'Outside recommended range';
  };

  const getValidationMessage = () => {
    if (errorMessage) return errorMessage;
    return getGuidelineMessage();
  };

  const isFocused = isSalaryFocused || isPercentageFocused;

  const handleConfirm = () => {
    onConfirm?.(value, percentage);
    setLastConfirmed({ value, percentage });
    setIsSalaryFocused(false);
    setIsPercentageFocused(false);
  };

  const handleCancel = () => {
    setValue(lastConfirmed.value);
    setPercentage(lastConfirmed.percentage);
    setIsSalaryFocused(false);
    setIsPercentageFocused(false);
  };

  // Handle outside click to cancel editing
  useOutsideClick({
    ref: componentRef,
    handler: () => {
      // Only handle outside click if component is actually focused
      if (isFocused) {
        handleCancel();
      }
    },
  });

  return (
    <Box
      ref={componentRef}
      w="100%"
      h="100%"
      display="flex"
      flexDir="column"
      justifyContent="center"
      gap="2px"
      background="transparent"
      data-interactive="true"
      className="salary-percentage-input-container"
    >
      {/* Input Row */}
      <Flex flexDir="row" alignItems="center">
        {/* Value Input */}
        <Box width="100px">
          <InputV2
            isDashed
            value={isSalaryFocused ? value : `${value}${unitSuffix}`}
            leftComponent={currencySymbol ? <TextBodyMedium>{currencySymbol}</TextBodyMedium> : undefined}
            onChange={(e) => {
              // Remove unit suffix and keep only numbers
              let inputValue = e.target.value;
              if (unitSuffix && inputValue.endsWith(unitSuffix)) {
                inputValue = inputValue.slice(0, -unitSuffix.length);
              }
              const numericValue = Number(inputValue.replace(/[^0-9.-]/g, ''));
              handleValueChange(numericValue);
            }}
            inputMode="decimal"
            disabled={disabled}
            onFocus={() => setIsSalaryFocused(true)}
          />
        </Box>

        {/* Divider */}
        <Box borderRight="1px solid #CFD7DF" height="20px" />

        {/* Percentage Input */}
        <Box
          width={percentageInputWidth}
          sx={{
            input: {
              paddingLeft: '12px !important',
            },
          }}
        >
          <InputV2
            value={isPercentageFocused ? percentage : `${percentage}%`}
            isDashed
            onChange={(e) => {
              const numericPercentage = Number(e.target.value.replace('%', '').replace(/[^0-9.-]/g, ''));
              handlePercentageChange(numericPercentage);
            }}
            onFocus={() => setIsPercentageFocused(true)}
            inputMode="decimal"
            disabled={disabled}
          />
        </Box>

        {/* Check/Cancel icons */}
        {isFocused && (
          <Flex alignItems="center" gap="4px" ml="8px" data-interactive="true">
            <Check width="24px" height="24px" color="#0D819F" style={{ cursor: 'pointer' }} onClick={handleConfirm} />
            <Close width="24px" height="24px" color="#C62C02" style={{ cursor: 'pointer' }} onClick={handleCancel} />
          </Flex>
        )}
      </Flex>

      {/* Guideline Validation */}
      {showGuideline && (guidelineMin !== undefined || guidelineMax !== undefined || isValid || errorMessage) && (
        <Flex alignItems="center" gap="4px">
          {isOutsideGuideline ? <StockTickerNegative /> : <StockTickerPositive />}
          <TextLabelSmall bold color={isOutsideGuideline ? '#C62C02' : '#1A5724'}>
            {getValidationMessage()}
          </TextLabelSmall>
        </Flex>
      )}
    </Box>
  );
};

export default SalaryPercentageInput;
